import MobileModal from '@/shared/ui/MobileModal';
import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ActiveTaskCard } from '@/components/dumb/activeTaskCard';
import { AllMeetings } from '@/components/icons/allMeetings';
import { CoffeeIcon } from '@/components/icons/coffeeIcon';
import { ModalCalendar } from '@/components/icons/ModalCalendar';
import { OnWeekIcon } from '@/components/icons/onWeek';
import { PlusIcon } from '@/components/icons/plusIcon';
import { SearchIcon } from '@/components/icons/searchIcon';
import { Simplification } from '@/components/icons/Simplification';
import { TomorrowIcon } from '@/components/icons/TomorrowIcon';
import { XMarkIcon } from '@/components/icons/xMark/XMarkIcon';
import { useTaskCard } from '@/features/tasks/model/modal/useTaskCards';
import { useTaskSelectionModalLogic } from '@/features/tasks/model/modal/useTaskSelectionModalLogic';
import { useNavigate } from 'react-router-dom';
import { taskDetailPath } from '@/entities/task/model/task-route';

export const TaskSelectionModalMobile = () => {
  const logic = useTaskSelectionModalLogic();
  const navigate = useNavigate();

  const ITEMS_PER_PAGE = 4;

  const allTabTasks = logic.tasksByTab?.[logic.activeTab] ?? [];
  const [page, setPage] = useState<number>(0);
  const [search, setSearch] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<string | number | null>(
    null
  );

  const query = search.trim().toLowerCase();
  const visibleTasks = useMemo(() => {
    if (!query) {
      return allTabTasks;
    }
    return allTabTasks.filter((task) => {
      const title = String(task.title ?? '').toLowerCase();
      const project = String(task.project?.Title ?? '').toLowerCase();
      return title.includes(query) || project.includes(query);
    });
  }, [allTabTasks, query]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(visibleTasks.length / ITEMS_PER_PAGE));
  }, [visibleTasks.length]);

  const safePage = Math.min(page, totalPages - 1);

  const pagedTasks = useMemo(() => {
    const start = safePage * ITEMS_PER_PAGE;
    return visibleTasks.slice(start, start + ITEMS_PER_PAGE);
  }, [visibleTasks, safePage]);

  const canGoPrev = safePage > 0;
  const canGoNext = safePage < totalPages - 1;

  const goPrevPage = () => {
    if (canGoPrev) {
      setPage((p) => Math.max(0, p - 1));
    }
  };

  const goNextPage = () => {
    if (canGoNext) {
      setPage((p) => p + 1);
    }
  };

  // Сбрасываем страницу/поиск при смене таба и при открытии модалки
  useEffect(() => {
    setPage(0);
    setSelectedTaskId(null);
    setSearch('');
  }, [logic.activeTab, logic.isOpen]);

  useEffect(() => {
    setPage(0);
  }, [query]);

  const mode = logic.mode;

  const title =
    mode === 'intermediate-timeup'
      ? 'Время на промежуточные дела закончилось. Опишите, что сделали!'
      : mode === 'complete'
        ? 'Задача выполнена, необходимо выбрать свое следующее действие!'
        : 'Задача приостановлена, необходимо выбрать свое следующее действие!';

  const subtitle =
    mode === 'intermediate-timeup'
      ? 'Обязательно опишите результат — он сохранится в задачу, затем выберите следующее действие.'
      : mode === 'pause'
        ? 'Если переключаешься на другую задачу — заполни в текстовое поле причину.'
        : 'Начинай следующую задачу либо сделай перерыв. Дважды начав на выбор — он активизируется.';

  return (
    <MobileModal
      open={logic.isOpen}
      onClose={logic.closeModal}
      containerClassName="bg-white text-[#14222C] font-roboto"
    >
      <div className="relative w-full max-w-[358px] flex flex-col gap-[24px] px-[12px] py-[24px]">
        <button
          type="button"
          onClick={logic.closeModal}
          aria-label="Закрыть"
          className="absolute right-[12px] top-[12px] z-10 flex h-[28px] w-[28px] items-center justify-center rounded-full bg-[#F8F7FC] text-[#14222C] transition hover:bg-[#ECEAF4]"
        >
          <XMarkIcon color="#14222C" width={12} height={12} />
        </button>

        {/* ===== Шапка: инструкция + возврат ===== */}
        <div className="flex flex-col gap-[20px] rounded-[17px] bg-[#F8F7FC] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)] px-[11px] py-[14px]">
          <div className="flex flex-col gap-[9px]">
            <p className="m-0 text-[16px] leading-[130%] text-[#14222C]">
              {title}
            </p>
            <p className="m-0 text-[10px] leading-[130%] text-[#575757]">
              {subtitle}
            </p>
          </div>

          {mode !== 'pause' && (
            <button
              type="button"
              onClick={logic.handleResumeTask}
              disabled={logic.isRenewing || !logic.canActIntermediate}
              className="self-end text-right text-[10px] leading-[130%] underline text-[#575757] transition hover:text-[#14222C] disabled:opacity-40"
            >
              {mode === 'intermediate-timeup'
                ? 'Вернуться в завершенную задачу'
                : 'Возобновить задачу'}
            </button>
          )}
        </div>

        {/* ===== Следующая задача ===== */}
        {mode !== 'pause' && logic.nextTodayTask && (
          <div className="flex flex-col gap-[10px]">
            <div className="text-center text-[14px] leading-[130%] text-[#575757]">
              Следующая задача по сегодняшнему плану
            </div>
            <ActiveTaskCard
              task={logic.nextTodayTask}
              elapsedMs={logic.spentMs}
              plannedMs={logic.plannedMs}
              assignee={logic.assignee}
              createdBy={logic.createdByUser}
              previewImage={
                logic.activePreviewImage
                  ? {
                      downloadUrl: logic.activePreviewImage.downloadUrl,
                      name: logic.activePreviewImage.name,
                    }
                  : undefined
              }
              hideActions
              onOpen={() => {
                navigate(taskDetailPath(logic.nextTodayTask.id));
                logic.closeModal();
              }}
            />
          </div>
        )}

        {/* ===== Действия + комментарий ===== */}
        <div className="flex flex-col gap-[16px]">
          {/* Продлить план дела: работа продолжается, результат не требуем */}
          {logic.isIntermediateTimeup && (
            <MobileActionButton
              onClick={() => logic.handleExtendIntermediate(5)}
              disabled={logic.isBusy}
              icon={<PlusIcon color="#575757" width={16} height={16} />}
              label="Продлить на 5 мин"
            />
          )}
          <div className="grid grid-cols-2 gap-[16px]">
            <MobileActionButton
              onClick={() => logic.handleBreak(15)}
              disabled={!logic.canActIntermediate}
              icon={<CoffeeIcon fill="#292D32" width={16} height={16} />}
              label="Перерыв"
            />
            <MobileActionButton
              onClick={logic.handleCreateIntermediate}
              disabled={logic.isBusy || !logic.canActIntermediate}
              icon={<PlusIcon color="#575757" width={16} height={16} />}
              label={logic.isBusy ? 'Загрузка…' : 'Создать задачу'}
            />
          </div>

          <div className="rounded-[17px] bg-[#F8F7FC] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)] px-[11px] py-[14px] text-[14px] leading-[130%] tracking-[-0.5px] text-[#575757]">
            Если необходимо, выбери из других задач, но обязательно укажи
            причину изменения плана.
          </div>

          <div className="rounded-[17px] bg-[#F8F7FC] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)] px-[11px] py-[14px]">
            <textarea
              value={logic.pauseComment}
              onChange={(e) => logic.setPauseComment(e.target.value)}
              placeholder={
                logic.isIntermediateTimeup
                  ? 'Опишите результат — что конкретно сделали (обязательное поле)'
                  : 'Введите коментарий (обязательное поле для выбора одной из задач ниже)'
              }
              rows={2}
              className="w-full resize-none bg-transparent outline-none text-[10px] leading-[130%] text-[#14222C] placeholder:text-[rgba(87,87,87,0.6)]"
            />
          </div>
        </div>

        {/* ===== Табы + поиск + список + пагинация ===== */}
        <div className="flex flex-col gap-[20px]">
          <div className="flex items-start gap-[6px] w-full overflow-x-auto no-scrollbar py-[2px]">
            <Tab
              label="Сегодня"
              icon={<ModalCalendar width={12} height={12} />}
              active={logic.activeTab === 'today'}
              onClick={() => logic.setActiveTab('today')}
            />
            <Tab
              label="Завтра"
              icon={<TomorrowIcon width={12} height={12} />}
              active={logic.activeTab === 'tomorrow'}
              onClick={() => logic.setActiveTab('tomorrow')}
            />
            <Tab
              label="На неделе"
              icon={<OnWeekIcon width={12} height={12} />}
              active={logic.activeTab === 'week'}
              onClick={() => logic.setActiveTab('week')}
            />
            <Tab
              label="Отложенные"
              icon={<Simplification width={12} height={12} />}
              active={logic.activeTab === 'deferred'}
              onClick={() => logic.setActiveTab('deferred')}
            />
            <Tab
              label="Встречи"
              icon={<AllMeetings width={12} height={12} />}
              active={logic.activeTab === 'all'}
              onClick={() => logic.setActiveTab('all')}
            />
          </div>

          <div className="flex items-center gap-[8px] h-[36px] px-[11px] rounded-[16px] bg-[#F8F7FC] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)]">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по названии"
              className="min-w-0 flex-1 bg-transparent outline-none text-[10px] leading-[130%] text-[#14222C] placeholder:text-[rgba(87,87,87,0.6)]"
            />
            <SearchIcon
              color="#6E6E6E"
              width={14}
              height={14}
              className="shrink-0"
            />
          </div>

          {visibleTasks.length > 0 ? (
            <div className="w-full flex flex-col gap-[11px]">
              {pagedTasks.map((task) => (
                <TaskCardContainer
                  key={task.id}
                  task={task}
                  usersMap={logic.usersMap}
                  onStart={logic.handleStartFromCard}
                  onComplete={logic.handleCompleteFromCard}
                  selectedTaskId={selectedTaskId}
                  setSelectedTaskId={setSelectedTaskId}
                  navigateToTask={(taskId) => navigate(taskDetailPath(taskId))}
                />
              ))}
            </div>
          ) : (
            <div className="w-full h-[93px] rounded-[16px] bg-[#F8F7FC] flex items-center justify-center text-[14px] leading-[130%] tracking-[-0.5px] text-[#8A8A8A]">
              {query ? 'Ничего не найдено' : 'Нет задач'}
            </div>
          )}

          <div className="w-full flex justify-between items-center gap-[24px]">
            <button
              type="button"
              onClick={goPrevPage}
              disabled={!canGoPrev}
              className={`flex items-center gap-[5px] text-[14px] leading-[130%] tracking-[-0.5px] text-[#14222C] transition ${canGoPrev ? 'opacity-100' : 'opacity-20'}`}
            >
              <ChevronRightIcon className="w-[16px] h-[16px] rotate-180" />
              <span>Назад</span>
            </button>

            <button
              type="button"
              onClick={goNextPage}
              disabled={!canGoNext}
              className={`flex items-center gap-[5px] text-[14px] leading-[130%] tracking-[-0.5px] text-[#14222C] transition ${canGoNext ? 'opacity-100' : 'opacity-20'}`}
            >
              <span>Следующая страница</span>
              <ChevronRightIcon className="w-[16px] h-[16px]" />
            </button>
          </div>
        </div>
      </div>
    </MobileModal>
  );
};

const MobileActionButton = ({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex h-[43px] w-full items-center justify-center gap-[8px] rounded-[16px] bg-white border border-[#EAEAEC] shadow-[0px_1px_6px_rgba(0,0,0,0.06)] px-[12px] text-[12px] font-medium leading-[130%] text-[#575757] transition hover:bg-[#F8F7FC] disabled:cursor-not-allowed disabled:opacity-40"
  >
    <span className="shrink-0 flex items-center justify-center">{icon}</span>
    <span className="truncate">{label}</span>
  </button>
);

const Tab = ({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      shrink-0 box-border flex items-center gap-[6px]
      h-[32px] px-[12px] rounded-[16px]
      whitespace-nowrap text-[10px] leading-[130%]
      transition
      ${
        active
          ? 'bg-[linear-gradient(257.42deg,#152242_0.84%,#3B4D7F_100.65%)] text-white shadow-[0px_2px_8px_rgba(21,34,66,0.25)]'
          : 'bg-white text-[#000000] border border-[#EAEAEC]'
      }
    `}
  >
    <span className="shrink-0 flex items-center justify-center">{icon}</span>
    <span>{label}</span>
  </button>
);

const TaskCardContainer = ({
  task,
  usersMap,
  onStart,
  onComplete,
  selectedTaskId,
  setSelectedTaskId,
  navigateToTask,
}: {
  task: ANY;
  usersMap: Map<number, ANY>;
  onStart: (task: ANY) => void;
  onComplete: (taskId: string) => void;
  selectedTaskId: string | number | null;
  setSelectedTaskId: (id: string | number | null) => void;
  navigateToTask: (taskId: string | number) => void;
}) => {
  const { elapsedMs, plannedMs, previewImage, fullTask, mapTaskToActiveCard } =
    useTaskCard(task);

  const cardProps = mapTaskToActiveCard(
    {
      ...task,
      fileStats: task.fileStats ?? fullTask?.fileStats,
    },
    usersMap
  );

  const selected = selectedTaskId === task.id;

  const handlePause = () => {
    if (selected) {
      onStart(task);
      navigateToTask(task.id);
      return;
    }
    setSelectedTaskId(task.id);
  };

  return (
    <ActiveTaskCard
      {...cardProps}
      elapsedMs={elapsedMs}
      plannedMs={plannedMs}
      previewImage={
        previewImage
          ? { downloadUrl: previewImage.downloadUrl, name: previewImage.name }
          : undefined
      }
      selected={selected}
      onPause={handlePause}
      onComplete={() => onComplete(task.id)}
    />
  );
};
